<template>
  <v-container fluid>
    <v-row justify="center">
      <v-col cols="8" justify="center">
        <v-alert
          v-if="finalScore"
          text
          :type="finalClass"
        >
          Receipt status: <strong class="capitalize">{{finalScore}}</strong>
        </v-alert>
      </v-col>
    </v-row>
    <v-form
      ref="uploadForm"
      v-model="valid"
      class="bubble-form"
      v-if="!loading"
    >
    <v-row 
      justify="center" 
      alignment="center"
    >
      <v-col cols="8">
        <p class="country-note country-note-top text-center"><span class="highlighted">Please ensure you enter the name as it appears on your bank account.<br> All refunds are via bank transfer – please note you will need to supply your bank details.</span></p>
      </v-col>
    </v-row>
    <v-row
      justify="center"
      alignment="center"
    >
      <v-col cols="8">
        <v-text-field
          v-model="form.firstname"
          :rules="rules.firstName"
          label="First name"
          required
          solo
          flat
        ></v-text-field>
      </v-col>
      <v-col cols="8">
        <v-text-field
          v-model="form.lastname"
          :rules="rules.lastName"
          label="Last name"
          required
          solo
          flat
        ></v-text-field>
      </v-col>
    </v-row>
    <v-row
      justify="center"
      alignment="center"
    >
      <v-col cols="8">
        <v-text-field
          v-model="form.email"
          :rules="rules.emailInput"
          label="Email"
          required
          solo
          flat
        ></v-text-field>
      </v-col>
      <v-col cols="8">
        <v-text-field
          v-model="form.address1"
          :rules="rules.address1"
          label="Address 1"
          required
          solo
          flat
        ></v-text-field>
      </v-col>
    </v-row>
    <v-row
      justify="center"
      alignment="center"
    >
      <v-col cols="8">
        <v-text-field
          v-model="form.address2"
          :rules="rules.address2"
          label="Address 2"
          required
          solo
          flat
        ></v-text-field>
      </v-col>
      <v-col cols="8">
        <v-text-field
          v-model="form.address3"
          :rules="rules.address3"
          label="Town"
          required
          solo
          flat
        ></v-text-field>
      </v-col>
    </v-row>
    <v-row
      justify="center"
      alignment="center"
    >
      <v-col cols="8">
        <v-text-field
          v-model="form.postcode"
          :rules="rules.postcode"
          label="Postcode"
          required
          solo
          flat
        ></v-text-field>
      </v-col>
      <v-col cols="8">
        <v-autocomplete
          v-model="form.retailer"
          solo
          flat
          :items="retailers"
          label="Store"
          required
          :rules="rules.retailer"
        ></v-autocomplete>
      </v-col>
    </v-row>
    <v-row
      justify="center"
      alignment="center"
    >
      <v-col cols="8">
        <v-switch
          v-model="form.terms"
          label="I agree to the terms and conditions"
          required
          :rules="rules.terms"
          false-value="0"
          true-value="1"
        ></v-switch>
      </v-col>
    </v-row>
    <v-row
      justify="center"
      alignment="center"
    >
      <v-col cols="8">
        <v-file-input
          v-model="files"
          color="#2570B5"
          counter
          label="File Upload"
          multiple
          placeholder="Add Receipt"
          prepend-icon="mdi-image-plus"
          :accept="acceptedFiles"
          :error="fileError"
          solo
          flat
          :show-size="1000"
          @change="fileChanged()"
          @click:clear="removeImage()"
          >
            <template v-slot:selection="{ index, text }">
              <v-chip
              v-if="index < 2"
              color="#2570B5"
              dark
              label
              small
              >
              {{ text }}
              </v-chip>

              <span
              v-else-if="index === 2"
              class="overline grey--text text--darken-3 mx-2"
              >
              +{{ files.length - 2 }} File(s)
              </span>
            </template>
          </v-file-input>
      </v-col>     
    </v-row>
    <v-row
      justify="center"
      alignment="center"
    >
      <v-col cols="8">
        <v-btn color="#0e676d" rounded depressed x-large block @click="submitForm()" dark>Go</v-btn>
      </v-col>
    </v-row>
    </v-form>
    <v-row justify="center" v-if="loading">
      <v-col cols="auto">
        <v-list-item-group>
          <v-list-item>
            <v-list-item-icon>
              <v-icon v-text="firstStepIcon"></v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              Determine file type <span v-if="receipt.type" v-text="receipt.type"></span>
            </v-list-item-content>
          </v-list-item>
          <v-list-item>
            <v-list-item-icon>
              <v-icon v-text="secondStepIcon"></v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              Retrieve file meta data
            </v-list-item-content>
          </v-list-item>
          <v-list-item>
            <v-list-item-icon>
              <v-icon v-text="thirdStepIcon"></v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              Prepare File
            </v-list-item-content>
          </v-list-item>
          <v-list-item v-if="loadingPrepare==1 && !isPdf">
            <v-list-item-icon>
              <v-icon v-text="AWSStepIcon"></v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              AWS Rekognition
            </v-list-item-content>
          </v-list-item>
          <v-list-item v-if="loadingPrepare==1 && !isPdf">
            <v-list-item-icon>
              <v-icon v-text="GoogleStepIcon"></v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              Google Vision
            </v-list-item-content>
          </v-list-item>

          <v-list-item>
            <v-list-item-icon>
              <v-icon v-text="fourthStepIcon"></v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              Process File
            </v-list-item-content>
          </v-list-item>

        </v-list-item-group>
      </v-col>
      <v-col cols="4" v-if="uploadImage">
        <v-img
            :src="uploadImage"
            contain
            class="outline"
            align="center"
            max-height="400"
        >
          <template v-slot:placeholder>
            <v-progress-circular
                :size="70"
                :width="7"
                color="#2570B5"
                indeterminate
                ></v-progress-circular>
          </template>
        </v-img>
      </v-col>
    </v-row>
    <v-row justify="center" v-if="eval">
      <v-col cols="4">
        <v-expansion-panels>
          <v-expansion-panel>
            <v-expansion-panel-header>
              Evaluation Results
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <pre>{{eval}}</pre>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
      <v-col cols="4">
        <v-expansion-panels>
          <v-expansion-panel>
            <v-expansion-panel-header>
              Detected Text
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <pre>{{detectedText}}</pre>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>
    <v-row justify="center">
      <v-col cols="8">
        <v-expansion-panels v-if="metaData||(contentDataA || contentDataG)">
          <v-expansion-panel v-if="metaData">
            <v-expansion-panel-header>
              Meta Data
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <pre>{{metaData}}</pre>
            </v-expansion-panel-content>
          </v-expansion-panel>
          <v-expansion-panel v-if="contentDataA">
            <v-expansion-panel-header>
              Amazon Content Data
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <pre>{{contentDataA}}</pre>
            </v-expansion-panel-content>
          </v-expansion-panel>
          <v-expansion-panel v-if="contentDataG">
            <v-expansion-panel-header>
              Google Content Data
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <pre>{{contentDataG}}</pre>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>
   
    <v-dialog
      v-model="feedbackDialog"
      max-width="290"
      >
      <v-card>
          <v-card-text v-html="feedbackDialogText" class="text-center"></v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
              <v-btn
                  color="#2570B5"
                  dark
                  rounded
                  min-width="100"
                  @click="feedbackDialog = false"
              >
                  Ok
              </v-btn>
              <v-spacer></v-spacer>
          </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>

export default {
  name: 'UploadForm',
  data: () => ({
      valid: false,
      files: [],
      fileError: false,
      imageError: false,
      receipt: [],
      uploadImage: false,
      uploadPercentage: 0,
      loading: false,
      loadingS1: false,
      loadingS2: false,
      loadingS3: false,
      loadingS4: false,
      loadingRekognition: false,
      loadingVision: false,
      loadingPrepare: false,
      loadingProcess: false,
      fileProcessed: false,
      finalScore: 0,
      eval: false,
      isPdf: false,
      feedbackDialog : false,
      feedbackDialogText : '',
      uploadMessage: 'Uploading file',
      receiptScore: 0,
      metaData: false,
      contentDataA: false,
      contentDataG: false,
      detectedText: false,
      acceptedFiles: "image/png, image/jpg, image/jpeg, image/gif, image/bmp, image/webp, image/raw, image/tiff, application/pdf",
      retailers: ['Asda', 'Booths', 'Co-operative', 'Dunnes', 'Marks & Spencer', 'Morrisions', 'Ocado', "Sainsbury's", "Spar", "SuperValu", "Tesco", "Waitrose"],
      form: {
        firstname:'',
        lastname: '',
        email: '',
        address1: '',
        address2: '',
        address3: '',
        postcode: '',
        product: '',
        retailer: '',
        terms: 0
      },
      seasonedId: 0,
      rules: {
        emailInput: [
            v => !!v || 'Email is required',
            v => /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(v) || 'E-mail must be valid'
        ],
        emailConfirm: [
            v => !!v || 'Email Confirmation is required'
        ],
        terms:[
            v => !!v || 'You must agree to continue!'
        ],
        firstName: [
            v => !!v || 'First Name is required',
            v => v.length > 2 || 'First Name must be more than 2 characters'
        ],
        lastName: [
            v => !!v || 'Last Name is required',
            v => v.length > 2 || 'Last Name must be more than 2 characters'
        ],
        county: [
            v => !!v || 'County is required'
        ],
        address1: [
          v => !!v || 'First line of address is required'
        ],
        address2: [
          v => !!v || 'Second line of address is required'
        ],
        address3: [
          v => !!v || 'Town is required'
        ],
        postcode: [
          v => !!v || 'Postcode is required'
        ],
        retailer: [
          v => !!v || 'Please select a store'
        ]
      }
  }),
  methods: {
    submitForm: function() {
      var hasReceipt = false
      if(this.receipt.name) {
        hasReceipt = true
      }else{
        // error out
        this.fileError = true
      }
      if(this.$refs.uploadForm.validate() && hasReceipt) {
      // accepted file type check
        
          this.loading = true
          // file type
          this.loadingS1 = true
          // meta data
          this.loadingS2 = true
          // api process - get data
          this.loadingS3 = true
          this.loadingPrepare = true
          // match wins
          if(this.receipt.type != '' && this.acceptedFiles.includes(this.receipt.type)) {
            // file is present
            // file is of correct type
            this.loadingS1 = 1
            var formData = new FormData();
            formData.append('receipt', this.receipt);
            formData.append('firstName', this.form.firstname);
            formData.append('lastName', this.form.lastname);
            formData.append('email', this.form.email);
            formData.append('address1', this.form.address1);
            formData.append('address2', this.form.address2);
            formData.append('address3', this.form.address3);
            formData.append('postcode', this.form.postcode);
            formData.append('terms', this.form.terms);
            formData.append('product', this.form.product);
            formData.append('retailer', this.form.retailer);
            /// *** purchase date supplied by end user?
            formData.append('purchaseDate', '');
            /// *** api/createEntry -> seasonedId
            this.axios.post('/api/createEntry', formData).then((entryResponse)=>{
              console.log(entryResponse)
              if(entryResponse.data.success) {
                formData.append('seasonedId', entryResponse.data.payload.seasonedId)
                this.seasonedId = entryResponse.data.payload.seasonedId
                // record added to db
                // get file metadata
                // get file meta data - EXIF
                this.axios.post('/api/fileMetaData', formData).then((response2)=>{
                  if(response2.data.success) {
                    this.loadingS2 = 1
                    this.metaData = JSON.stringify(response2.data.payload.metaData, null, 2)
                  }else{
                    this.feedbackDialogText = response2.data.errors[0]                
                    this.feedbackDialog = true
                  }
                })
                // prepare file - convert if required
                this.axios.post('/api/filePrepare', formData).then((responsePrepare)=>{
                  if(responsePrepare.data.success) { 
                    this.loadingRekognition = true
                    this.loadingVision = true
                    this.loadingPrepare = 1
                    if(responsePrepare.data.payload.isPdf) {
                      // file is native PDF
                      this.isPdf = true
                    }else{
                      // file process
                      // amazon rekognition detectText
                      this.axios.post('/api/rekognition', formData).then((responseRekognition)=>{
                        this.loadingRekognition = 1
                        if(responseRekognition.data.success) {
                          this.loadingRekognition = 1
                          this.contentDataA = JSON.stringify(responseRekognition.data.payload.contentDataA, null, 2)
                        }else{
                          this.feedbackDialogText = responseRekognition.data.errors[0]                
                          this.feedbackDialog = true
                        }
                      })
                      // google vision textDetect
                      this.axios.post('/api/vision', formData).then((responseVision)=>{
                        this.loadingVision = 1
                        if(responseVision.data.success) {
                          this.loadingVision = 1
                          this.contentDataG = responseVision.data.payload.contentDataG
                          this.detectedText = this.contentDataG[0].textAnnotations[0].description
                        }else{
                          this.feedbackDialogText = responseVision.data.errors[0]                
                          this.feedbackDialog = true
                        }
                      })
                      // google vision receipt detection
                      this.axios.post('/api/isReceipt', formData).then((response3)=>{
                        if(response3.data.success) {
                          this.loadingS3 = 1
                          if(response3.data.payload.receipt) {
                            this.receiptScore = Math.round(response3.data.payload.receipt.score * 100)
                          }
                        }else{
                          this.feedbackDialogText = response3.data.errors[0]                
                          this.feedbackDialog = true
                        }
                      })
                    }
                  }else{
                    // handle errors
                    this.feedbackDialogText = responsePrepare.data.errors[0]                
                    this.feedbackDialog = true
                  }
                })
              }else{
                this.feedbackDialogText = entryResponse.data.errors              
                this.feedbackDialog = true
              }
            })
          }else{
            // error out
            this.feedbackDialogText = 'Unrecognised file type: '+this.receipt.type+'. Please ensure the uploaded file is one of the following: .png, .jpg, .gif, .bmp, .webp, .ico, .raw, .tiff, .pdf'                
            this.feedbackDialog = true
          }
        
      }
    },
    fileChanged : function () {
      this.$refs.uploadForm.validate();
      this.imageError = false;
      // Handle file as object
      // If multiple {file} is object array
      // Chain api calls and feedback to user
      if(this.files.length>0) {
        this.fileError = false
        var file = this.files[0]
        this.receipt = file;
        //var image = new Image();
        var reader = new FileReader();
        var vm = this;
        var extension = this.files[0].name.substring(this.files[0].name.lastIndexOf('.')).toLowerCase()
        console.log(extension)
        reader.onload = (e) => {
          if(file.type != 'application/pdf' && extension != '.heic') {
            vm.uploadImage = e.target.result;
          }else if (extension == '.heic') {
            vm.uploadImage = {src:require('../assets/heic-icon-256x256@2x.webp')}
          }else if(file.type.substring(0, file.type.lastIndexOf('/'))) {
            vm.uploadImage = {src:require('../assets/pdf-icon.png')}
          }else{
            this.feedbackDialogText = 'Unsupported file type. Please check and try again.'                
            this.feedbackDialog = true
          }
        };
        reader.readAsDataURL(file);
      }
    },
    removeImage: function() {
      this.uploadImage=false
      this.receipt = []
      this.files = []
      this.metaData = false
      this.contentDataA = false
      this.contentDataG = false
      this.loadingS1 = false
      this.loadingS2 = false
      this.loadingS3 = false
      this.loadingS4 = false
      this.loadingRekognition = false
      this.loadingVision = false
      this.loadingPrepare = false
      this.loadingProcess = false
      this.eval = false
      this.fileProcessed = false
    },
    processFile: function () {
      // fire call
      if(!this.fileProcessed) {
        this.fileProcessed = true
        this.axios.post('/api/matchData', {contentDataA:this.contentDataG[0].textAnnotations[0].description, seasonedId: this.seasonedId}).then((processResponse)=>{
          this.loadingProcess = true
          console.log(processResponse)
          if(processResponse.data.success) {
            this.eval = processResponse.data.payload.eval
            this.finalScore = processResponse.data.payload.status
            if(this.finalScore == 'Pass') {
              this.$router.push('/win')
            }else if(this.finalScore == 'Fail') {
              this.$router.push('/sorry')
            }else{
              this.$router.push('/wait')
            }
            this.loadingProcess = 1
          }else{
            // handle rerrors
            this.feedbackDialogText = processResponse.data.errors                
            this.feedbackDialog = true
          }
        })
      }
      // set flag
    }
  },
  watch : {
    uploadPercentage: function () {
      if(this.uploadPercentage==100) {
        this.uploadMessage = 'File uplaoded successfully'
      }else{
        this.uploadMessage = 'Uploading file'
      }
    },
    contentDataG: function () {
      if(this.contentDataG && this.contentDataA) {
        this.processFile()
      }
    },
    contentDataA: function () {
      if(this.contentDataG && this.contentDataA) {
        this.processFile()
      }
    }
  },
  computed: {
    finalClass: function() {
      if(this.finalScore=='pass') {
        return 'success'
      }else if(this.finalScore=='soft'){
        return 'warning'
      }else{
        return 'error'
      }
    },
    firstStepIcon:function() {
      switch(this.loadingS1) {
        case true:
          return 'mdi-circle-slice-1'
        break;
        case 1:
          return 'mdi-check-circle-outline'
        break;
        case 2:
          return 'mdi-alert-circle-outline'
        break;
        default:
          return 'mdi-circle-outline'
      }
    },
    secondStepIcon:function() {
      switch(this.loadingS2) {
        case true:
          return 'mdi-circle-slice-1'
        break;
        case 1:
          return 'mdi-check-circle-outline'
        break;
        case 2:
          return 'mdi-alert-circle-outline'
        break;
        default:
          return 'mdi-circle-outline'
      }
    },
    thirdStepIcon:function() {
      switch(this.loadingPrepare) {
        case true:
          return 'mdi-circle-slice-1'
        break;
        case 1:
          return 'mdi-check-circle-outline'
        break;
        case 2:
          return 'mdi-alert-circle-outline'
        break;
        default:
          return 'mdi-circle-outline'
      }
    },
    fourthStepIcon:function() {
      switch(this.loadingProcess) {
        case true:
          return 'mdi-circle-slice-1'
        break;
        case 1:
          return 'mdi-check-circle-outline'
        break;
        case 2:
          return 'mdi-alert-circle-outline'
        break;
        default:
          return 'mdi-circle-outline'
      }
    },
    GoogleStepIcon: function () {
      switch(this.loadingVision) {
        case true:
          return 'mdi-circle-slice-1'
        break;
        case 1:
          return 'mdi-check-circle-outline'
        break;
        case 2:
          return 'mdi-alert-circle-outline'
        break;
        default:
          return 'mdi-circle-outline'
      }
    },
    AWSStepIcon: function () {
      switch(this.loadingRekognition) {
        case true:
          return 'mdi-circle-slice-1'
        break;
        case 1:
          return 'mdi-check-circle-outline'
        break;
        case 2:
          return 'mdi-alert-circle-outline'
        break;
        default:
          return 'mdi-circle-outline'
      }
    }
  }
};
</script>

<style>
  .v-input__append-outer .theme--light.v-icon, h1{
    color:#2570B5; 
  }
  pre {
	width: 100%;
	padding: 0;
	margin: 0;
	overflow: auto;
	overflow-y: hidden;
	font-size: 12px;
	line-height: 20px;
	background: #efefef;
	border: 1px solid #777;
}
pre code {
	padding: 10px;
	color: #333;
}

.mdi-circle-slice-1 {
    -webkit-animation:spin 3s linear infinite;
    -moz-animation:spin 3s linear infinite;
    animation:spin 3s linear infinite;
}
@-moz-keyframes spin { 100% { -moz-transform: rotate(360deg); } }
@-webkit-keyframes spin { 100% { -webkit-transform: rotate(360deg); } }
@keyframes spin { 100% { -webkit-transform: rotate(360deg); transform:rotate(360deg); } }
.theme--light.v-icon.mdi-check-circle-outline {
  color:darkgreen;
}
.theme--light.v-icon.mdi-circle-slice-1 {
  color:#2570B5;
}
.capitalize {
  text-transform: capitalize;
}
.error--text + .v-file-input__text--placeholder{
  color: #ff5252 !important;
  caret-color: #ff5252 !important;
}
</style>